/**
 * FieldWrapper Component
 * Shared field chrome component for consistent styling and accessibility
 */

import React from 'react';
import styled, { css } from 'styled-components';

// ============================================================================
// Styled Components
// ============================================================================

const FormGroup = styled.div`
  margin-bottom: 1rem;
  position: relative;
`;

const Label = styled.label<{ required?: boolean; disabled?: boolean }>`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.9rem;
  line-height: 1.4;

  ${(props) =>
    props.disabled &&
    css`
      color: var(--text-muted);
      opacity: 0.7;
    `}

  ${(props) =>
    props.required &&
    css`
      &::after {
        content: ' *';
        color: var(--danger-600);
        font-weight: bold;
      }
    `}
`;

const ErrorText = styled.span`
  color: var(--danger-600);
  font-size: 0.85rem;
  margin-top: 0.25rem;
  display: block;
  line-height: 1.4;
  font-weight: 500;
`;

const HelperText = styled.span<{ hasError?: boolean }>`
  font-size: 0.85rem;
  color: ${(props) =>
    props.hasError ? 'var(--danger-600)' : 'var(--text-muted)'};
  margin-top: 0.25rem;
  display: block;
  line-height: 1.4;
`;

// ============================================================================
// Component Interface
// ============================================================================

export interface FieldWrapperProps {
  id: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  error?: string | undefined;
  helperText?: string | undefined;
  className?: string | undefined;
  children: React.ReactNode;
}

// ============================================================================
// FieldWrapper Component
// ============================================================================

export const FieldWrapper: React.FC<FieldWrapperProps> = ({
  id,
  label,
  required = false,
  disabled = false,
  error,
  helperText,
  className,
  children,
}) => {
  const errorId = `${id}-error`;
  const helperId = `${id}-helper`;
  const hasError = !!error;

  return (
    <FormGroup className={className}>
      <Label htmlFor={id} required={required} disabled={disabled}>
        {label}
      </Label>

      {children}

      {hasError && (
        <ErrorText id={errorId} role="alert" aria-live="polite">
          {error}
        </ErrorText>
      )}

      {helperText && !hasError && (
        <HelperText id={helperId}>{helperText}</HelperText>
      )}
    </FormGroup>
  );
};

export default FieldWrapper;
